import { useState, useEffect } from 'react';
import { getCardBankName, InnocodePaymentCard, InnocodePaymentCardInfo, InnocodePaymentHistoryList, InnocodePaymentListHorizental } from '@intsoft/innocode-payment';
import { InnocodeBadge, InnocodeButton, InnocodePaginationGroup, InnocodeTextButton, useDialog, useModal } from '@intsoft/innocode-core';
import { PaymentStatusGroup, PaymentType, getPaymentTypeColor, getPaymentTypeLabel } from '../../../resources/utils/paymentUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { SubscriptionStopModal } from '../../../resources/modal/SubscriptionStopModal';
import { SubscriptionCancleModal } from '../../../resources/modal/SubscriptionCancleModal';
import { MainCardSelectModal } from '../../../resources/modal/MainCardSelectModal';
import styles from './SubscriptionDetail.module.css';
import { ZENPAY_BILLING, ZENPAY_PAYMENT, ZENPAY_SUBSCRIPTION } from '../../../utils/Config';
import axios from "../../../utils/api/axios";
import moment from 'moment';
import { getSubscriptionStatusColor, getSubscriptionStatusLabel, SubscriptionStatus } from '../../../resources/utils/subscriptionUtils';
import { getCookie } from '../../../utils/cookies/cookies';
import { BankType, CardType, mapApiBrandToCardType, mapApiIssuerToBankType } from '../../../resources/utils/bank-types';
import { usePasswordChangeModal } from '../../NavBar/component/usePasswordChangeModal';

type DialogType = 'confirm' | 'delete' | 'error' | 'info';
type DialogIconType = 'check' | 'info' | 'warning';

interface ApiCardData {
  id: number;
  cardNumber: string;
  brand: string;      // ex: "마스터", "비자"
  issuer: string;     // ex: "KB국민카드"
  statementFactorName?: string; // ex: "카카오"
  userId: string;
  cardType: string;
  cardName: string;
  billingKey: string;
}

export interface cardInfo {
  id: string;
  cardNumber?: string;
  cardType: string;
  cardBrand: string;
  cardBankType?: string;
  cardBrandName?: string;
  cardName?: string;
  billingKey?: string;
  status: "active" | "inactive";
}

interface subscriptionInfo {
  subscriptionId: string;
  subscriptionStatus: SubscriptionStatus;
  subscriptionAmount: number;
  subscriptionTitle?: string;
  subscriptionDescription?: string;
  subscriptionStoreName?: string;
  subscriptionStoreBadgeLabel?: string;
  subscriptionThumbnailUrl?: string;
  subscriptionThumbnailBadgeLabel?: string;
  subscriptionRenewalDate?: string;
}

interface paymentCardInfo {
  type: 'card' | 'register';
  cardNumber: string;
  cardBankType: string;
  cardBankName?: string;
  cardType: string;
}


interface paymentHistoryInfo {
  paymentAmount: number;
  paymentTitle?: string;
  receiptUrl?: string;
}

export default function Page() {
  const { showInnocodeModal: showBottomModal } = useModal();
  const [platform, setPlatform] = useState<'web' | 'tablet-vertical' | 'mobile'>('web');
  const { showDialog } = useDialog();
  const [enableDeleteMode, setEnableDeleteMode] = useState(false);
  const navigate = useNavigate();

  const { handleOpenModal, isLoading } = usePasswordChangeModal();

  // 페이지네이션 상태
  const listSize = 10;
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const { subscriptionId } = useParams();

  const [subscriptionItem, setSubscriptionItem] = useState<subscriptionInfo>(
    {
      subscriptionId: '',
      subscriptionStatus: SubscriptionStatus.PAUSED,
      subscriptionAmount: 0,
      subscriptionTitle: '',
      subscriptionDescription: '',
      subscriptionStoreName: '',
      subscriptionStoreBadgeLabel: '',
      subscriptionThumbnailUrl: '',
      subscriptionThumbnailBadgeLabel: '',
      subscriptionRenewalDate: '',
    }
  );

  const [paymentCard, setPaymentCard] = useState<paymentCardInfo>(
    {
      type: 'card',
      cardNumber: '',
      cardBankType: '',
      cardBankName: '',
      cardType: '',
    }
  );

  const [paymentHistoryList, setPaymentHistoryList] = useState<paymentHistoryInfo[]>([

  ]);

  const [cardList, setCardList] = useState<cardInfo[]>([]);
  const [lastPaymentDate, setLastPaymentDate] = useState<number>(0);


  const toggleDeleteMode = () => {
    setEnableDeleteMode(!enableDeleteMode);
  };

  const getPlatform = (width: number): 'web' | 'tablet-vertical' | 'mobile' => {
    if (width < 768) return 'mobile';
    if (width < 1440) return 'tablet-vertical';
    return 'web';
  };

  useEffect(() => {
    // 초기 사이즈 설정 
    setPlatform(getPlatform(window.innerWidth));
    // 윈도우 리사이즈 이벤트 핸들러
    const handleResize = () => {
      setPlatform(getPlatform(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);

    // 클린업 함수
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    getSubscriptionDetail();
  }, []);


  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    if (page < 0 || page >= totalPages) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    getPaymentHistoryList(currentPage);
  }, [currentPage]);

  const getSubscriptionDetail = async () => {
    try {
      const subscriptionDetailRes = await axios.get(`${ZENPAY_SUBSCRIPTION}/${subscriptionId}`);
      const subscriptionData = subscriptionDetailRes.data;

      const subscriptionDescription = `${subscriptionData.optionName} / ${subscriptionData.optionValue}`;
      //data를 subscriptionInfo에 맞게 가공
      const subscriptionInfo: subscriptionInfo = {
        subscriptionId: subscriptionData.subscriptionId,
        subscriptionStatus: subscriptionData.status,
        subscriptionAmount: subscriptionData.paymentAmount,
        subscriptionTitle: subscriptionData.productName,
        subscriptionDescription: subscriptionDescription,
        subscriptionStoreName: subscriptionData.marketName,
        subscriptionThumbnailUrl: subscriptionData.thumbnailUrl,
        subscriptionRenewalDate: moment(subscriptionData.renewalDate).format('YYYY.MM.DD'),
      };

      setSubscriptionItem(subscriptionInfo);
      setLastPaymentDate(subscriptionData.renewalDate);


      const url = ZENPAY_BILLING + "/all";
      const conditions = [
        { field: "userId", operator: "EQUALS", value: getCookie("user_id") }
      ];
      const params = {
        page: 0,
        size: 0,
        sortField: ['createdDate'],
        sortOrder: ['asc'],
        operator: "AND",
        conditions
      };

      const res = await axios.post(url, params);

      const { data, recordsTotal } = res.data.data;
      const apiCardDataList: ApiCardData[] = data;
      const currentBillingKey = subscriptionData.paymentInfo.billingKey;


      // API 데이터를 CardInfo[] 형태로 변환
      const transformedCardList: cardInfo[] = apiCardDataList.map((apiCard) => {

        const cardBankType = mapApiIssuerToBankType(apiCard.issuer, apiCard.statementFactorName);
        const cardBrand = mapApiBrandToCardType(apiCard.brand);

        const cardName = getCardBankName(cardBankType); // ex: "신한카드"
        // 제공된 라이브러리 헬퍼 함수들을 사용
        const status = apiCard.billingKey === currentBillingKey ? 'active' : 'inactive';

        return {
          id: String(apiCard.id),
          cardNumber: apiCard.cardNumber,
          cardType: apiCard.cardType === "체크" ? "Check" : "",
          cardBrand: cardBrand,
          cardBankType: cardBankType,
          cardName: apiCard.cardName || '기타 카드', // getCardBankName이 빈 문자열을 반환할 경우 대비
          cardBrandName: `${cardName || '기타'} ${cardBrand === CardType.DEFAULT ? '카드' : cardBrand}`, // 예시 로직
          billingKey: apiCard.billingKey,
          status: status,
        };
      });

      //transformedCardList에 billingKey가 있는지 확인
      const card = transformedCardList.find((card) => card.billingKey === currentBillingKey);

      setCardList(transformedCardList);
      const paymentCard: paymentCardInfo = {
        type: 'card',
        cardNumber: card?.cardNumber || '',
        cardType: card?.cardType || CardType.DEFAULT,
        cardBankType: card?.cardBankType || BankType.DEFAULT,
      };

      setPaymentCard(paymentCard);
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const getPaymentHistoryList = async (page: number) => {
    try {
      const url = ZENPAY_PAYMENT + "/all";
      const conditions = [
        { field: "subscriptionId", operator: "EQUALS", value: subscriptionId },
        { field: "paymentStatus", operator: "NOT_EQUALS", value: PaymentType.PENDING },
        { field: "isDeleteHistory", operator: "EQUALS", value: false }
      ];
      const params = {
        page: page,
        size: listSize,
        sortField: ['paymentPaidDate'],
        sortOrder: ['desc'],
        operator: "AND",
        conditions
      };

      const res = await axios.post(url, params);
      const { data, recordsTotal } = res.data.data;

      //data는 paymentHistoryInfo 맞게 가공이 필요
      const transformedPaymentHistoryList: paymentHistoryInfo[] = data.map((payment: any) => {

        const paymentDate = moment(payment.paymentPaidDate).format('YYYY년 MM월 DD일');
        const title = paymentDate + ' ' + payment.paymentInfo.orderName;
        return {
          paymentId: payment.id,
          paymentDate: paymentDate,
          paymentAmount: payment.totalAmount,
          paymentTitle: title,
          receiptUrl: payment.receiptUrl,
        };
      });

      setPaymentHistoryList(transformedPaymentHistoryList);
      setTotalPages(Math.max(1, Math.ceil(recordsTotal / listSize)));

    } catch (error) {
      console.error(error);
    } finally {
    }
  };


  const subscriptionCancle = () => {

    let dialogType: DialogType = 'delete';
    let dialogIconType: DialogIconType = 'warning';
    let dialogTitle = '구독을 해지하시겠어요?';
    let dialogMessage = '구독을 해지하지 않고\n정지상태로 변경할 수 있어요';
    let dialogCancelText = '해지';
    let dialogConfirmText = '일시 정지';
    if (subscriptionItem!.subscriptionStatus === SubscriptionStatus.PAUSED) {
      dialogTitle = '구독을 해지하시겠어요?';
      dialogMessage = '구독을 해지하지 않고\n일시정지를 해제할 수 있어요.\n일시정지 해제 시 바로 구독이 다시 시작됩니다.';
      dialogCancelText = '해지';
      dialogConfirmText = '취소';
    }

    showDialog({
      type: dialogType,
      iconType: dialogIconType,
      title: dialogTitle,
      message: dialogMessage,
      cancelText: dialogCancelText,
      confirmText: dialogConfirmText,
      onCancel: () => {
        showBottomModal({
          enableDefaultStyle: true,
          headerBuilder: () => { return <></>; },
          contentBuilder: (
            _data: any,
            _handleSave: any,
            _handleDelete: any,
            _handleUpdate: any,
            handleClose: any,
            _modalType: any
          ) => {
            return <SubscriptionCancleModal
              isOpen={true}
              onClose={handleClose}
              onConfirm={async (data: any) => {
                console.log("[SubscriptionCancleModal] " + data.reason, data.otherReason);
                handleClose();

                await axios.post(`${ZENPAY_SUBSCRIPTION}/${subscriptionId}/cancel`, {
                  reason: data.reason,
                  otherReason: data.otherReason
                });

                showDialog({
                  type: 'info',
                  iconType: 'check',
                  title: '구독이 해지되었습니다.',
                  message: `소중한 의견 감사합니다.\n보내주신 의견으로 더 발전하는\n[${subscriptionItem!.subscriptionStoreName}]가 되겠습니다.\n\n저희 멤버십은 언제든 다시 이용 가능하니\n다음에도 또 찾아주세요!`,
                  confirmText: '완료',
                  onConfirm: () => {
                    window.history.back()
                  }
                });
              }}

            />;
          },
        });
      },
      onConfirm: async () => {
        if (subscriptionItem!.subscriptionStatus === SubscriptionStatus.ACTIVE) {
          showBottomModal({
            enableDefaultStyle: true,
            headerBuilder: () => { return <></>; },
            contentBuilder: (
              _data: any,
              _handleSave: any,
              _handleDelete: any,
              _handleUpdate: any,
              handleClose: any,
              _modalType: any
            ) => {
              return <SubscriptionStopModal
                isOpen={true}
                onClose={handleClose}
                renewalDate={lastPaymentDate}
                onConfirm={async (data: any) => {
                  const period = data.period;
                  const stopDate = data.stopDate;
                  const subscriptionRenewalDate = data.subscriptionRenewalDate;

                  await axios.patch(`${ZENPAY_SUBSCRIPTION}/${subscriptionId}/stop`, {
                    status: SubscriptionStatus.PAUSED,
                    renewalDate: subscriptionRenewalDate,
                    stopDate: stopDate
                  });

                  showDialog({
                    type: 'info',
                    iconType: 'check',
                    title: '구독이 일시정지 되었습니다.',
                    message: `일시 정지 기간: ${period}개월\n일시 정지 해제일: ${stopDate}`,
                    confirmText: '완료',
                    onConfirm: () => {
                      window.history.back();
                    }
                  });
                }}
              />;
            },
          });
        }
      }
    });
  }

  const subscriptionPauseCancle = () => {

    let dialogType: DialogType = 'confirm';
    let dialogIconType: DialogIconType = 'info';
    let dialogTitle = '구독을 다시 시작 하시겠어요?';
    let dialogMessage = '일시정지를 해제하고 구독을 다시 시작할 수 있어요.';
    let dialogCancelText = '취소';
    let dialogConfirmText = '구독시작';

    showDialog({
      type: dialogType,
      iconType: dialogIconType,
      title: dialogTitle,
      message: dialogMessage,
      cancelText: dialogCancelText,
      confirmText: dialogConfirmText,
      onCancel: () => {
      },
      onConfirm: async () => {
        const result = await axios.patch(`${ZENPAY_SUBSCRIPTION}/${subscriptionId}/unsuspended`);
        console.log(result);
        if (result.data.code === 200) {
          showDialog({
            type: 'info',
            iconType: 'check',
            title: '구독 일시정지 해제 완료',
            message: `구독 일시정지 해제가 정상적으로 완료되었습니다. \n구독이 정상적으로 시작되었습니다.`,
            confirmText: '확인',
            onConfirm: () => {
              window.history.back();
            }
          });
        }
      }
    });
  }

  const handlePaymentMethodChange = () => {
    showBottomModal({
      enableDefaultStyle: true,
      headerBuilder: () => { return <></>; },
      contentBuilder: (
        _data: any,
        _handleSave: any,
        _handleDelete: any,
        _handleUpdate: any,
        handleClose: any,
        _modalType: any
      ) => {
        return <MainCardSelectModal
          title="결제 수단 변경"
          confirmText="변경하기"
          isOpen={true}
          onClose={handleClose}
          cardList={cardList}
          onConfirm={(selectedCard: cardInfo) => {
            handleClose();

            showDialog({
              type: 'confirm',
              iconType: 'info',
              title: '선택하신 카드를 결제수단으로\n변경하시겠어요?',
              confirmText: '예',
              cancelText: '아니요',
              onConfirm: async () => {
                handleOpenModal({
                  selectMode: 'verify',
                  onSuccess: async () => {
                    const res = await axios.patch(`${ZENPAY_SUBSCRIPTION}/${subscriptionId}/billing-key`, {
                      billingKey: selectedCard.billingKey,
                    });

                    if (res.data.code === 200) {
                      showDialog({
                        type: 'info',
                        iconType: 'check',
                        title: '결제수단이 변경되었습니다.',
                        confirmText: '완료',
                        onConfirm: () => {
                          getSubscriptionDetail();
                        }
                      })
                    } else {
                      showDialog({
                        type: 'error',
                        iconType: 'warning',
                        title: '결제수단이 변경하지 못했습니다.\n다시 시도해주세요.',
                        confirmText: '오류',
                        onConfirm: () => {

                        }
                      })
                    }
                  }
                });

              }
            })
          }}
        />;
      },
    });
  }

  const handleReceiptView = (receiptUrl: string) => {
    // receipt url로 이동
    window.open(receiptUrl, '_blank');
  }

  return (
    <div className="content-wrapper">
      <div className={styles.pageWrapper}>
        <div className={styles.titleSection}>
          {platform === "web" && (
            <InnocodeButton iconOnly={true} onClick={() => window.history.back()} designType='assistive' theme="opacity">
              <svg {...{ slot: "icon" }} className="w-6 h-6 text-gray-800 " aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m15 19-7-7 7-7" />
              </svg>
            </InnocodeButton>
          )}
          <h3 className="innocode-typography--headline-sm innocode-typography--bold">구독 상세 정보</h3>
        </div>
        <div className={styles.contentSection}>
          <div className={styles.subscriptionInfo}>

            <div className={`${styles.subscriptionItemWrapper} ${styles.boxWrapper}`} >
              <h3 className="innocode-typography--semibold innocode-typography--body-lg-reading">구독 상품</h3>
              <div className={styles.subscriptionItem}>
                <InnocodePaymentListHorizental
                  key={subscriptionItem!.subscriptionId}
                  className="list_item"
                  size={"md"}
                  status="default"
                  showPaymentStatus={true}
                  showPaymentDetail={platform === "web" ? false : true}
                  paymentStatus={getSubscriptionStatusLabel(subscriptionItem!.subscriptionStatus)}
                  paymentStatusColor={getSubscriptionStatusColor(subscriptionItem!.subscriptionStatus)}
                  showTopContents={false}
                  leadingThumbnail={true}
                  thumbnailSrc={subscriptionItem!.subscriptionThumbnailUrl || 'https://storage.innocode.co/api/v1/storage/files/content/c6289fce-c576-489b-9fc1-5235d580184f'}
                  thumbnailAlt="상품 이미지"
                  showThumbnailBadge={true}
                  thumbnailBadgeStyle='solid'
                  thumbnailBadgeLabel={subscriptionItem!.subscriptionThumbnailBadgeLabel || ''}
                  thumbnailSize={platform === "web" ? "md" : "sm"}
                  showStoreInfo={true}
                  showStoreName={true}
                  showStoreBadge={true}
                  storeBadgeStyle='opacity'
                  storeName={subscriptionItem!.subscriptionStoreName || ''}
                  storeBadgeLabel={subscriptionItem!.subscriptionStoreBadgeLabel || ''}
                  showTitle={true}
                  title={subscriptionItem!.subscriptionTitle || ''}
                  showDescription={true}
                  description={subscriptionItem!.subscriptionDescription || ''}
                  showBottomContents={true}
                  showAmount={true}
                  amount={subscriptionItem!.subscriptionAmount}
                  showPaymentDate={true}
                  paymentDate={subscriptionItem!.subscriptionRenewalDate ? `다음 결제일: ${subscriptionItem!.subscriptionRenewalDate}` : ''}
                  onClick={() => {
                    // navigate(`/zentry/subscription/detail/${subscriptionItem.subscriptionId}`);
                  }}
                />
              </div>
            </div>
            <div className={`${styles.subscriptionPaymentMethodWrapper} ${styles.boxWrapper}`} >
              <div className={styles.subscriptionTitleSection}>
                <h3 className="innocode-typography--semibold innocode-typography--body-lg-reading">현재 결제 수단</h3>
                <InnocodeTextButton trailingIcon={true} type={"assistive"} size={"sm"} onClick={handlePaymentMethodChange}>
                  결제 수단 변경
                  <svg {...{ slot: "trailing-icon" }} className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m10 16 4-4-4-4" />
                  </svg>
                </InnocodeTextButton>
              </div>
              <div className={styles.subscriptionPaymentMethod}>
                {paymentCard.cardNumber !== '' && (
                  <>
                    {platform === "web" && (
                      <InnocodePaymentCard
                        cardBankType={paymentCard!.cardBankType}
                        cardNumber={`${paymentCard!.cardNumber} ${paymentCard!.cardType}`}
                        cardType={paymentCard!.cardType}

                      ></InnocodePaymentCard>
                    )}
                    {platform !== "web" && (
                      <InnocodePaymentCardInfo
                        size="sm"
                        cardBankType={paymentCard!.cardBankType}
                        cardNumber={`${paymentCard!.cardNumber} ${paymentCard!.cardType}`}
                        cardType={paymentCard!.cardType}
                        cardName={paymentCard!.cardBankName}
                      ></InnocodePaymentCardInfo>
                    )}
                  </>
                )}

              </div>
            </div>
          </div>
          <div className={`${styles.paymentHistoryInfo} ${styles.boxWrapper}`}>
            <div className={styles.subscriptionTitleSection}>
              <h3 className="innocode-typography--semibold innocode-typography--body-lg-reading">결제 내역</h3>
            </div>
            <div className={styles.paymentHistoryList}>
              {paymentHistoryList.map((item, index) => (
                <InnocodePaymentHistoryList
                  key={index}
                  status="default"
                  showTitle={true}
                  title={item.paymentTitle}
                  showAmount={true}
                  amount={item.paymentAmount}
                  enablePadding={true}
                  onClick={() => {
                    handleReceiptView(item.receiptUrl!);
                  }}
                ></InnocodePaymentHistoryList>
              ))}
            </div>
            <InnocodePaginationGroup
              width={100}
              totalPages={paymentHistoryList.length === 0 ? 1 : totalPages}
              currentPage={currentPage + 1}
              onPageChange={(page: number) => {
                handlePageChange(page - 1);
              }}
              onPrevious={() => {
                handlePageChange(currentPage - 1)
              }}
              onNext={() => {
                handlePageChange(currentPage + 1);
              }}
              onFirst={() => {
                handlePageChange(0);
              }}
              onLast={() => {
                handlePageChange(totalPages - 1);
              }}
              showNavigation={true}
              showFirstLast={true}
              showEllipsis={true}
              pageRange={10}
            ></InnocodePaginationGroup>

          </div>
          <div className={styles.subscriptionCancelBox}>
            <InnocodeTextButton
              type="assistive"
              size="sm"
              onClick={subscriptionCancle}
              className={styles.subscriptionCancelActionButton}
            >
              구독 해지
            </InnocodeTextButton>
            {(subscriptionItem!.subscriptionStatus === SubscriptionStatus.PAUSED || subscriptionItem!.subscriptionStatus === SubscriptionStatus.CANCELED_WAIT) && (
              <InnocodeTextButton
                type="assistive"
                size="sm"
                onClick={subscriptionPauseCancle}
                className={styles.subscriptionCancelActionButton}
              >
                일시정지 해제
              </InnocodeTextButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
